import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { useCountdown } from "~/hooks/useCountdown"
import { useJoinedSpace } from "~/hooks/useJoinedSpace"
import { useActiveSpacePage } from "~/providers/ActiveSpacePageProvider"
import { getUser } from "~/store"
import { formattedDuration } from "~/utils/TimeFormatter"
import {
  animateTimerFaviconIn,
  setCycleFavicon,
  setTimerFavicon,
} from "~/utils/Favicon"
import { playAlarm } from "~/utils/AlarmUtils"
import { showTimerEndedNotification } from "~/utils/NotificationUtils"
import { postEvent } from "~/utils/EventManager"
import { Timer } from "~/types"

export default function GlobalTimerEffects() {
  const user = useSelector(getUser)
  const joinedSpace = useJoinedSpace()
  const activeSpacePage = useActiveSpacePage()

  // A page presenting a space owns the timer experience (title, favicon,
  // sound) — bail out completely.
  const timer: Timer | null =
    activeSpacePage || !joinedSpace?.current_timer
      ? null
      : joinedSpace.current_timer

  const currentTimerId = joinedSpace?.current_timer?.id ?? null
  const durationLeft = useCountdown(timer)
  const [isAnimatedIn, setIsAnimatedIn] = useState(false)
  const [endedTimer, setEndedTimer] = useState<Timer | null>(null)
  const completedTimerIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!timer) {
      setIsAnimatedIn(false)
      return
    }

    let cancelled = false
    animateTimerFaviconIn(timer, 3000).then(() => {
      if (!cancelled) setIsAnimatedIn(true)
    })

    return () => {
      cancelled = true
      document.title = "Pomodoro"
      setCycleFavicon(timer.break ? "break" : "work")
    }
  }, [timer?.id])

  useEffect(() => {
    if (!timer || durationLeft === null) return
    if (durationLeft > 0) {
      document.title = formattedDuration(durationLeft)
      if (isAnimatedIn) setTimerFavicon(timer)
    } else {
      document.title = "Pomodoro"
      setCycleFavicon(timer.break ? "break" : "work")
    }
  }, [timer, durationLeft, isAnimatedIn])

  // Keyed on the timer id rather than on a countdown transition, so a timer
  // that already expired before this component saw it is still completed.
  useEffect(() => {
    if (!timer || durationLeft !== 0 || !user?.space_name) return
    if (completedTimerIdRef.current === timer.id) return

    completedTimerIdRef.current = timer.id

    postEvent({ spaceName: user.space_name }, "timerEnded", {
      timer_id: timer.id,
    })
    if (user.alert && user.alert_volume != null) playAlarm(user.alert_volume)
    showTimerEndedNotification(timer)
    setEndedTimer(timer)
  }, [timer, durationLeft, user?.space_name, user?.alert, user?.alert_volume])

  // After a timer ends, keep flipping the tab title until a new timer
  // starts or the user navigates back to the space page.
  useEffect(() => {
    if (!endedTimer) return

    // The ended timer lingers in the store until the server confirms it, so
    // only a different id means someone started a new one.
    if (currentTimerId && currentTimerId !== endedTimer.id) {
      setEndedTimer(null)
      return
    }

    let isFlipped = false
    const interval = setInterval(() => {
      document.title = isFlipped ? "Pomodoro timer ended" : "Pomodoro"
      isFlipped = !isFlipped
    }, 3000)

    return () => {
      clearInterval(interval)
      document.title = "Pomodoro"
    }
  }, [endedTimer, currentTimerId])

  // When the user navigates to a page presenting a space, hand control back
  // to that page and stop the title-flip reminder.
  useEffect(() => {
    if (activeSpacePage) setEndedTimer(null)
  }, [activeSpacePage])

  return null
}
